<template>
  <template v-if="element.tag === '#text'">
    {{ element.text }}
  </template>
  <span
    v-else-if="element.tag === 'span'"
    :class="[element.attributes?.class, 'font-bold']"
    v-bind="spanAttributes">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </span>
  <Typography
    v-else-if="['div', 'p'].includes(element.tag)"
    :as="element.tag"
    :variant="bodyVariant"
    :class="element.attributes?.class"
    class="not-last:mb-8">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </Typography>
  <Typography
    v-else-if="element.tag === 'em'"
    as="em"
    :variant="bodyVariant"
    :class="element.attributes?.class">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </Typography>
  <Typography
    v-else-if="element.tag === 'strong'"
    as="strong"
    :variant="bodyVariant"
    :class="element.attributes?.class">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </Typography>
  <Typography
    v-else-if="element.tag === 'h2'"
    as="h2"
    variant="header-2"
    :class="element.attributes?.class"
    class="not-last:mb-4">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </Typography>
  <Typography
    v-else-if="element.tag === 'h3'"
    as="h3"
    variant="header-3"
    :class="element.attributes?.class"
    class="not-last:mb-2">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </Typography>
  <br v-else-if="element.tag === 'br'" >
  <Link
    v-else-if="element.tag === 'a'"
    :to="linkTo(element)"
    :title="element.attributes.title"
    :target="element.attributes.target"
    :class="element.attributes.class"
    :variant="bodyVariant"
    underline>
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </Link>
  <ul
    v-else-if="element.tag === 'ul' && (element.attributes?.style as string | undefined)?.includes('list-style-type: square')"
    class="not-last:mb-8 ml-0 flex flex-col gap-2 list-none checkmark-list">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </ul>
  <ul
    v-else-if="element.tag === 'ul' && (element.attributes?.style as string | undefined)?.includes('list-style-type: circle')"
    class="not-last:mb-8 ml-0 flex flex-col gap-2 list-none dash-list">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </ul>
  <ul
    v-else-if="element.tag === 'ul'"
    :class="element.attributes?.class"
    class="not-last:mb-8 ml-5 flex flex-col gap-2 list-disc marker:pr-3 marker:font-700">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </ul>
  <ol v-else-if="element.tag === 'ol'" :class="element.attributes?.class" class="not-last:mb-8 ml-0 flex flex-col gap-2 list-none counter-reset-list">
    <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
  </ol>
  <Typography
    v-else-if="element.tag === 'li'"
    as="li"
    :variant="bodyVariant"
    :class="element.attributes?.class"
    class="mb-0 pl-2">
    <div class="inline">
      <RichTextElements :elements="element.elements" :body-variant="bodyVariant" />
    </div>
  </Typography>
</template>

<script setup lang="ts">
import type { UmbracoRichTextElement } from '@gro-up/types'
import { Link, Typography } from '@gro-up/ui/primitives'
import { inject, computed } from 'vue'

import type { TypographyVariant } from '../Typography/Typography.vue'

import RichTextElements from './RichTextElements.vue'

const props = defineProps<{
  element: UmbracoRichTextElement
  bodyVariant?: TypographyVariant
}>()

const injectedBodyVariant = inject('bodyVariant', 'body')
const bodyVariant = computed(() => props.bodyVariant ?? injectedBodyVariant)

const spanAttributes = computed(() => {
  const { class: _class, ...rest } = props.element.attributes ?? {}
  return rest
})

// Anchor is preferred over queryString, mirroring the backend's LinkMapper.AppendAnchorOrQuery.
const linkTo = (element: UmbracoRichTextElement) => {
  if (element.tag !== 'a') {
    return undefined
  }

  const { href, anchor, route } = element.attributes
  if (href) {
    return href
  }

  if (route?.path) {
    return `${route.path}${anchor || route.queryString || ''}`
  }

  return undefined
}
</script>

